/**
 * Contract for the commit-attribution endpoints. Imported by:
 *   - excellent/app/api/commit-attr/commits/**     (server)
 *   - packages/ui/src/blocks/excellent/task-drawer.tsx + new commit blocks (client)
 *
 * Shapes mirror excellent_commits + excellent_task_commits in
 * excellent/src/projects/migrations.ts (v23). If you add a column there,
 * add it here too.
 */

import { z } from "zod";
import type { Operation } from "../types";
import {
  idNumericParams,
  paginationQuery,
  paginatedSchema,
} from "./_common";

export const commitRowSchema = z.object({
  id: z.number(),
  sha: z.string(),
  parent_sha: z.string().nullable().optional(),
  author_email: z.string().nullable().optional(),
  author_name: z.string().nullable().optional(),
  author_actor_id: z.number().nullable().optional(),
  committing_actor_id: z.number().nullable().optional(),
  committed_at: z.string(),
  subject: z.string().nullable().optional(),
  rewritten_from_sha: z.string().nullable().optional(),
  repo_id: z.number(),
});
export type CommitRow = z.infer<typeof commitRowSchema>;

/** Sortable columns (whitelist mirrored server-side). */
export const commitSort = z.enum(["committed", "author", "subject"]);

export const commitsListQuery = paginationQuery.extend({
  task_id: z.coerce.number().int().optional(),
  repo_id: z.coerce.number().int().optional(),
  author_actor_id: z.coerce.number().int().optional(),
  committing_actor_id: z.coerce.number().int().optional(),
  sort: commitSort.optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});

export const commitsList: Operation<
  void,
  z.infer<typeof commitsListQuery>,
  void,
  z.infer<ReturnType<typeof paginatedSchema<typeof commitRowSchema>>>
> = {
  method: "GET",
  path: () => "/api/commit-attr/commits",
  query: commitsListQuery,
  response: paginatedSchema(commitRowSchema),
};

export const commitLinkBody = z.object({
  task_id: z.number().int(),
  link_kind: z.enum(["auto_in_progress", "message_ref", "manual"]).optional(),
});

export const commitLink: Operation<
  { id: number },
  void,
  z.infer<typeof commitLinkBody>,
  { id: number; created: boolean }
> = {
  method: "POST",
  path: ({ id }) => `/api/commit-attr/commits/${id}/link`,
  params: idNumericParams,
  body: commitLinkBody,
  response: z.object({ id: z.number(), created: z.boolean() }),
  // task_id filter membership on the list family moves.
  invalidates: [commitsList],
};

export const commitUnlinkBody = z.object({ task_id: z.number().int() });

export const commitUnlink: Operation<
  { id: number },
  void,
  z.infer<typeof commitUnlinkBody>,
  { deleted: boolean }
> = {
  method: "DELETE",
  path: ({ id }) => `/api/commit-attr/commits/${id}/link`,
  params: idNumericParams,
  body: commitUnlinkBody,
  response: z.object({ deleted: z.boolean() }),
  invalidates: [commitsList],
};

export const commits = {
  list: commitsList,
  link: commitLink,
  unlink: commitUnlink,
};
